import type { ApiResponse, ApiErrorResponse } from './types'

// ============================================
// API Configuration
// ============================================
// 주의: state/atoms.ts의 API_URLS와 동일하게 유지할 것 (순환 import 때문에 중복 정의)
const API_URLS = {
  development: 'https://api-dev.abcpay.co.kr',
  production: 'https://api.abcpay.co.kr',
} as const

const BUILD_ENVIRONMENT: 'development' | 'production' =
  import.meta.env.MODE === 'development' ? 'development' : 'production'

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || API_URLS[BUILD_ENVIRONMENT]

// Debug: Check env variables are loaded
console.log('🔍 API Config:', {
  environment: BUILD_ENVIRONMENT,
  baseUrl: API_BASE_URL
})

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status?: number
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// ============================================
// API Client
// ============================================
export class ApiClient {
  private _baseUrl: string
  private token: string | null = null

  constructor(baseUrl: string = API_BASE_URL) {
    this._baseUrl = baseUrl.replace(/\/$/, '') // Remove trailing slash
  }

  /**
   * Set authentication token
   */
  setToken(token: string | null): void {
    this.token = token
    console.log('🔑 API token updated:', token ? 'Set' : 'Cleared', "token: " + token)
  }

  /**
   * Set API base URL
   */
  setBaseUrl(url: string): void {
    this._baseUrl = url.replace(/\/$/, '')
    console.log('🌐 API base URL updated:', this._baseUrl)
  }

  /**
   * Get current base URL
   */
  get baseUrl(): string {
    return this._baseUrl
  }

  /**
   * Make API request with standard error handling
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this._baseUrl}${endpoint}`

    // Default headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
      ...options.headers,
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      })

      // DEBUG: Log response details
      console.log('📥 API Response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      })

      // Handle 401 Unauthorized - token expired
      if (response.status === 401) {
        console.warn('🔒 Unauthorized: Token expired or invalid')
        window.dispatchEvent(new CustomEvent('auth:expired'))
        throw new ApiError('UNAUTHORIZED', 'Session expired. Please login again.', 401)
      }

      // Parse JSON response
      const data = await response.json() as ApiResponse<T>

      // DEBUG: Log parsed data
      console.log('📦 RAW Response:', JSON.stringify(data, null, 2))

      // Check for API error response
      if ('error' in data) {
        const errorData = data as ApiErrorResponse
        throw new ApiError(
          errorData.error.code,
          errorData.error.message || 'API request failed',
          response.status
        )
      }

      // Return success data
      // API returns data wrapped in 'data' key OR direct object
      if ('data' in data) {
        return data.data
      }

      // If no 'data' or 'error' key, return the whole response
      // (API might return direct object like { user: ... })
      console.log('⚠️  No data/error key found, returning raw response')
      return data as T

    } catch (error) {
      // Network errors or fetch failures
      if (error instanceof ApiError) {
        throw error
      }

      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError(
          'NETWORK_ERROR',
          'Network connection failed. Please check your internet connection.',
          0
        )
      }

      // Unknown errors
      throw new ApiError(
        'UNKNOWN_ERROR',
        error instanceof Error ? error.message : 'Unknown error occurred',
        0
      )
    }
  }

  /**
   * POST request (all API endpoints use POST)
   */
  async post<T>(endpoint: string, body?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  /**
   * GET request (for future use if needed)
   */
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
    })
  }
}

// Default singleton instance
export const apiClient = new ApiClient()
